import { useState, useEffect } from "react";
import api from "../lib/api";
import { Panel, Stat } from "../components/common";
import { fmt } from "../lib/format";
import { LineChart as LC, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { LineChart, Play, ShieldCheck } from "lucide-react";

function Metric({ label, value, unit = "", good }) {
  const color = good == null ? "#F8FAFC" : good ? "#10B981" : "#EF4444";
  return (
    <div className="panel-2 rounded-md p-3">
      <div className="eyebrow mb-1">{label}</div>
      <div className="font-mono font-extrabold text-lg tnum" style={{ color }}>{value}{unit}</div>
    </div>
  );
}

export default function Portfolio() {
  const [lookback, setLookback] = useState(63);
  const [topN, setTopN] = useState(6);
  const [rebalance, setRebalance] = useState(21);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = () => {
    setLoading(true);
    api.post("/backtest", { lookback, top_n: topN, rebalance })
      .then((r) => setRes(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const m = res?.metrics;
  return (
    <div className="space-y-5" data-testid="portfolio-page">
      <div>
        <div className="eyebrow">regime-aware portfolio engine</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Portfolio Backtest</h1>
      </div>

      <Panel title="Strategy Configuration" icon={<LineChart size={13} />}
        right={<div className="flex items-center gap-1.5 text-[11px] font-mono text-[#10B981]"><ShieldCheck size={13} /> point-in-time · no look-ahead</div>}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <div className="eyebrow mb-1">momentum lookback (days): {lookback}</div>
            <input data-testid="bt-lookback" type="range" min="21" max="252" step="21" value={lookback} onChange={(e) => setLookback(Number(e.target.value))} className="w-full accent-[#00E5FF]" />
          </div>
          <div>
            <div className="eyebrow mb-1">portfolio size (top N): {topN}</div>
            <input data-testid="bt-topn" type="range" min="2" max="10" value={topN} onChange={(e) => setTopN(Number(e.target.value))} className="w-full accent-[#00E5FF]" />
          </div>
          <div>
            <div className="eyebrow mb-1">rebalance every: {rebalance}d</div>
            <input data-testid="bt-rebalance" type="range" min="5" max="63" step="1" value={rebalance} onChange={(e) => setRebalance(Number(e.target.value))} className="w-full accent-[#00E5FF]" />
          </div>
          <button data-testid="backtest-run-button" onClick={run} disabled={loading}
            className="flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold text-[#04121A] transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: "#00E5FF" }}>
            <Play size={15} /> {loading ? "Running…" : "Run Backtest"}
          </button>
        </div>
      </Panel>

      {m && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="backtest-metrics">
            <Metric label="CAGR" value={fmt(m.cagr, 1)} unit="%" good={m.cagr > m.benchmark_cagr} />
            <Metric label="Sharpe" value={fmt(m.sharpe)} good={m.sharpe > 1} />
            <Metric label="Sortino" value={fmt(m.sortino)} good={m.sortino > 1} />
            <Metric label="Max Drawdown" value={fmt(m.max_drawdown, 1)} unit="%" good={m.max_drawdown > -25} />
            <Metric label="Calmar" value={fmt(m.calmar)} good={m.calmar > 1} />
            <Metric label="Volatility" value={fmt(m.volatility, 1)} unit="%" />
            <Metric label="Win Rate" value={fmt(m.win_rate, 1)} unit="%" good={m.win_rate > 50} />
            <Metric label="Profit Factor" value={fmt(m.profit_factor)} good={m.profit_factor > 1} />
            <Metric label="Alpha" value={fmt(m.alpha, 1)} unit="%" good={m.alpha > 0} />
            <Metric label="Beta" value={fmt(m.beta)} />
            <Metric label="Turnover" value={fmt(m.turnover, 0)} unit="%" />
            <Metric label="Bench CAGR" value={fmt(m.benchmark_cagr, 1)} unit="%" />
          </div>

          <Panel title="Equity Curve — Strategy vs SPY Benchmark" icon={<LineChart size={13} />}>
            <ResponsiveContainer width="100%" height={340}>
              <LC data={res.curve} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid stroke="#1B243B" vertical={false} />
                <XAxis dataKey="t" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} minTickGap={48} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" }} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Line type="monotone" dataKey="strategy" name="Strategy" stroke="#00E5FF" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="benchmark" name="SPY" stroke="#64748B" dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
              </LC>
            </ResponsiveContainer>
            <div className="mt-3 eyebrow">
              engine: trailing-momentum rotation · equal-weight top {res.config.top_n} · rebalance {res.config.rebalance}d · transaction costs modeled · universe {res.config.universe.length} assets
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
